<script setup lang="ts">
import { useCookies } from "@/assets/js/cookie";

const cookies = useCookies();

const draftAnalytics = ref(cookies.consent.analytics);
const draftGoogleMaps = ref(cookies.consent.googleMaps);

watch(
	() => cookies.cookieNoticeVisible.value,
	(visible) => {
		if (visible) {
			draftAnalytics.value = cookies.consent.analytics;
			draftGoogleMaps.value = cookies.consent.googleMaps;
		}
	},
	{ immediate: true },
);

function acceptAll() {
	cookies.acceptAll();
}

function rejectAll() {
	cookies.rejectAll();
}

function savePreferences() {
	cookies.savePreferences({
		analytics: draftAnalytics.value,
		googleMaps: draftGoogleMaps.value,
	});
}
</script>

<template>
	<Teleport to="body">
		<transition name="cookie-notice-fade">
			<div
				v-if="cookies.cookieNoticeVisible.value"
				class="cookie-notice"
				role="dialog"
				aria-live="polite"
				aria-label="Cookie preferences"
			>
				<div class="cookie-notice__text">
					<!-- TODO: copy -->
				</div>

				<div class="cookie-notice__toggles">
					<label class="cookie-toggle">
						<input type="checkbox" checked disabled />
						<span>Necessary</span>
					</label>

					<label class="cookie-toggle">
						<input v-model="draftGoogleMaps" type="checkbox" />
						<span>Google Maps</span>
					</label>

					<label class="cookie-toggle">
						<input v-model="draftAnalytics" type="checkbox" />
						<span>Analytics</span>
					</label>
				</div>

				<div class="cookie-notice__actions">
					<button
						type="button"
						class="cookie-btn cookie-btn--ghost"
						@click="rejectAll"
					>
						Reject all
					</button>
					<button
						type="button"
						class="cookie-btn cookie-btn--ghost"
						@click="savePreferences"
					>
						Save preferences
					</button>
					<button
						type="button"
						class="cookie-btn cookie-btn--primary"
						@click="acceptAll"
					>
						Accept all
					</button>
				</div>
			</div>
		</transition>
	</Teleport>
</template>

<style lang="scss" scoped>
.cookie-notice {
	position: fixed;
	z-index: 1000;
	left: 50%;
	bottom: 24px;
	transform: translateX(-50%);
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: calc(100% - 32px);
	max-width: 720px;
	padding: 20px 24px;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 8px 32px rgb(0 0 0 / 20%);
	font-family: Archivo, sans-serif;
	color: $text-color;

	&__toggles {
		display: flex;
		flex-wrap: wrap;
		gap: 16px;
	}

	&__actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-end;
		gap: 12px;
	}
}

.cookie-toggle {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	cursor: pointer;

	input[disabled] {
		cursor: not-allowed;
	}
}

.cookie-btn {
	height: $button-height;
	padding: 0 20px;
	border: 1px solid $app-color;
	border-radius: $button-border-radius;
	background: transparent;
	color: $app-color;
	font-family: Archivo, sans-serif;
	font-weight: 600;
	cursor: pointer;
	transition: opacity 0.2s ease;

	&:hover {
		opacity: 0.8;
	}

	&--primary {
		background: $app-color;
		color: #fff;
	}
}

.cookie-notice-fade-enter-active,
.cookie-notice-fade-leave-active {
	transition:
		opacity 0.3s ease,
		transform 0.3s ease;
}

.cookie-notice-fade-enter-from,
.cookie-notice-fade-leave-to {
	opacity: 0;
	transform: translate(-50%, 16px);
}
</style>
